import { QueryFunctionContext, queryOptions } from "@tanstack/react-query";
import { ASSESSMENT_SERVICE_URL, fetchWrapper } from "../index";
import {
  Assessment,
  AssessmentAnswer,
  AssessmentCollection,
  AssessmentResult,
  DraftAssessment,
  EvaluationType,
  Metrics,
  Outcomes,
  PublicGetTenderPackageAssessmentsResponseItem,
  Question,
  TenderPackageAssessmentResults,
  type AssignedCollection,
  type Section,
} from "./types";
import { isFeatureFlagOn } from "@/service/featureFlags.ts";
import {
  Client,
  refreshToken,
  setOrgHeader,
  setWorkspaceHeader,
} from "../client.ts";
import { getWorkspace } from "@/context/index.ts";
import { createApiClient, type ApiTransport } from "@supply25/http/client";
import { reassessmentsApi } from "@supply25/shared/assessment/reassessmentsApi";

const client = new Client(ASSESSMENT_SERVICE_URL, {
  before: [refreshToken(), setOrgHeader, setWorkspaceHeader],
  credentials: "include",
  headers: { "content-type": "application/json" },
});

const publicClient = new Client(ASSESSMENT_SERVICE_URL, {
  before: [],
  headers: { "content-type": "application/json" },
});

const send: ApiTransport = (request) =>
  client.send(request.method, `/${request.path.replace(/^\//, "")}`, {
    ...(request.body && typeof request.body === "object"
      ? { json: request.body }
      : {}),
  });

/**
 * Typed client for the reassessments api schema: call signatures come from
 * each route's input, responses are runtime-parsed with its output.
 */
export const reassessments = createApiClient(reassessmentsApi, send);

export const listContractReassessmentsQuery = (contractId: string) => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [organisation.id, workspace.id, contractId, "reassessments"],
    queryFn: () => reassessments.list({ params: { contractId } }),
    select: ({ data }) => data,
  });
};

export const listAssessmentReassessmentsQuery = ({
  contractId,
  collectionId,
  assessmentId,
}: {
  contractId: string;
  collectionId: string;
  assessmentId: string;
}) => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [
      organisation.id,
      workspace.id,
      contractId,
      "reassessments",
      collectionId,
      assessmentId,
    ],
    queryFn: () =>
      reassessments.listByAssessment({
        params: { contractId, collectionId, assessmentId },
      }),
    select: ({ data }) => data,
  });
};

export const getReassessmentResponseQuery = ({
  contractId,
  urlId,
}: {
  contractId: string;
  urlId: string;
}) => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [
      organisation.id,
      workspace.id,
      contractId,
      "reassessments",
      urlId,
      "response",
    ],
    queryFn: () => reassessments.response({ params: { contractId, urlId } }),
    select: ({ data }) => data,
  });
};

export const listReassessmentMessagesQuery = ({
  contractId,
  urlId,
}: {
  contractId: string;
  urlId: string;
}) => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [
      organisation.id,
      workspace.id,
      contractId,
      "reassessments",
      urlId,
      "messages",
    ],
    queryFn: () =>
      reassessments.listMessages({ params: { contractId, urlId } }),
    select: ({ data }) => data,
  });
};

type listPublishedAssessmentsCtx = QueryFunctionContext<
  [string, "publishedAssessments"]
>;

export const listPublishedAssessments = async ({
  queryKey,
}: listPublishedAssessmentsCtx) => {
  const [organisationId] = queryKey;
  return client.get("/published?global=true", {
    headers: { "Organisation-Id": organisationId },
  }) as Promise<{
    data: AssessmentCollection[];
  }>;
};

interface CollectionInput {
  collectionId: string;
  organisationId?: string;
  workspaceId?: string;
}

interface AssignAssessmentsToTenderPackage {
  collections: CollectionInput[];
  tenderPackageId: string;
  organisationId: string;
  workspaceId?: string;
}
export const assignAssessmentsToTenderPackage = async ({
  collections,
  tenderPackageId,
  organisationId,
  workspaceId,
}: AssignAssessmentsToTenderPackage) => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Organisation-Id", organisationId);

  if (isFeatureFlagOn("workspace") && workspaceId) {
    headers.append("Workspace-Id", workspaceId);
  }

  const res = await fetchWrapper(
    `${ASSESSMENT_SERVICE_URL}/tender-package/${tenderPackageId}`,
    {
      method: "POST",
      headers: headers,
      body: JSON.stringify({
        collections,
      }),
      credentials: "include",
    }
  );

  return res;
};

type GetPackageAssessmentsCtx = QueryFunctionContext<
  [string, string, "packages", string, "assessments"]
>;

export const getPackageAssessments = ({
  queryKey,
}: GetPackageAssessmentsCtx) => {
  const [orgId, workspaceId, , tenderPackageId] = queryKey;
  return client.get(`/tender-package/${tenderPackageId}`, {
    headers: { "Organisation-Id": orgId, "Workspace-Id": workspaceId },
  }) as Promise<{ data: AssignedCollection[] }>;
};

type GetResultsCtx = QueryFunctionContext<
  [string, string, "packages", string, "results"]
>;

export const getPackageResults = ({ queryKey }: GetResultsCtx) => {
  const [orgId, workspaceId, , tenderPackageId] = queryKey;
  return client.get(`/tender-package/${tenderPackageId}/result`, {
    headers: { "Organisation-Id": orgId, "Workspace-Id": workspaceId },
  }) as Promise<{ data: TenderPackageAssessmentResults }>;
};

interface ImprovementDecision {
  supplierId: string;
  supplierWorkspaceId?: string;
  questionId: string;
  urlId: string;
  attempt: number;
  decision: string;
  reason: string;
}

export const saveImprovementDecision = (json: ImprovementDecision) => {
  return client.post(`/improvement-action/decision`, { json });
};

export const publicGetPackageCollectionsQuery = (packageId: string) =>
  queryOptions({
    queryKey: ["collection", packageId, "assessments"],
    queryFn: () =>
      publicClient.get(`/public/tender-package/${packageId}`) as Promise<{
        data: PublicGetTenderPackageAssessmentsResponseItem[];
      }>,
    select: (res) => res.data,
  });

export const packageAssessmentResultsQuery = (packageId: string) => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [
      organisation.id,
      workspace.id,
      "package",
      packageId,
      "assessments",
      "results",
    ],
    queryFn: () =>
      client.get(`/tender-package/${packageId}/my-result`) as Promise<{
        data: AssessmentResult[];
      }>,
    select: (res) => res.data,
  });
};

interface ExportResultAsPdf {
  urlId: string;
  tenderName: string;
  referenceId: string;
  collectionName: string;
  isLateSubmission?: boolean;
  supplierOrganisationId?: string;
  supplierWorkspaceId?: string;
  supplierOrganisationName?: string;
}

export const exportResultAsPdf = async ({
  urlId,
  tenderName,
  referenceId,
  collectionName,
  isLateSubmission,
  supplierOrganisationId,
  supplierWorkspaceId,
  supplierOrganisationName,
}: ExportResultAsPdf) => {
  const res = await client.post(`/result/export`, {
    body: JSON.stringify({
      urlId,
      tenderId: referenceId,
      assessmentPackageName: tenderName,
      isLateSubmission,
      supplierOrganisationId,
      supplierWorkspaceId,
      supplierOrganisationName,
    }),
    credentials: "include",
  });

  // TODO: This api actually doesn't return data. We should change this in the backend, but it's too much for now
  const { url } = (await res) as { url: string };

  const s3headers = new Headers();
  s3headers.append("Content-Type", "application/json");
  const s3Res = await fetch(url, { headers: s3headers });
  if (!s3Res.ok) {
    throw new Error(`Error when fetching pdf from S3.`);
  }

  const blob = await s3Res.blob();
  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = supplierOrganisationName
    ? `${tenderName} - ${supplierOrganisationName} - ${collectionName} Supplier Submission.pdf`
    : `${tenderName} - ${collectionName} Supplier Submission.pdf`; // Use the file name from the key
  link.click();
  URL.revokeObjectURL(downloadUrl); // Clean up
};

export const getPackageMetrics = ({
  queryKey,
}: QueryFunctionContext<[string, string, "metrics"]>) => {
  const [orgId, tenderPackageId] = queryKey;
  return client.get(`/tender-package/${tenderPackageId}/metrics`, {
    headers: { "Organisation-Id": orgId },
  }) as Promise<Metrics>;
};

export const listQuestions = async ({
  queryKey,
}: QueryFunctionContext): Promise<{ data: Question[] }> => {
  const [organisationId, workspaceId] = queryKey;

  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Organisation-Id", organisationId as string);

  if (isFeatureFlagOn("workspace") && workspaceId) {
    headers.append("Workspace-Id", workspaceId as string);
  }

  const res = await fetchWrapper(`${ASSESSMENT_SERVICE_URL}/question`, {
    headers: headers,
    method: "GET",
    credentials: "include",
  });

  return res;
};

export const listAssessmentCollections = async ({
  queryKey,
}: QueryFunctionContext<[string, string, string]>): Promise<{
  data: AssessmentCollection[];
  evaluationBreakdown: Record<
    NonNullable<Assessment["evaluationType"]> | "total",
    number
  >;
}> => {
  const [organisationId, workspaceId] = queryKey;

  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Organisation-Id", organisationId);
  headers.append("Workspace-Id", workspaceId);

  const res = await fetchWrapper(`${ASSESSMENT_SERVICE_URL}/collection`, {
    headers: headers,
    method: "GET",
    credentials: "include",
  });

  return res;
};

export const collectionVersionsQuery = (collectionId: string) => {
  const { organisation, workspace } = getWorkspace();

  return queryOptions({
    queryKey: [
      organisation.id,
      workspace.id,
      "collections",
      collectionId,
      "versions",
    ] as const,
    queryFn: () =>
      client.get(`/collection/${collectionId}/versions`) as Promise<{
        data: AssessmentCollection[];
      }>,
    select: ({ data }) => data,
  });
};

type CollectionKey = readonly [string, string, "collections", string];

export const getAssessmentCollection = async ({
  queryKey,
}: QueryFunctionContext<CollectionKey>) => {
  const [, , , collectionId] = queryKey;

  return client.get(`/collection/${collectionId}`) as Promise<{
    data: AssessmentCollection;
  }>;
};

interface CreateAssessmentCollection {
  organisationId: string;
  workspaceId: string;
  name: string;
  collectionType: AssessmentCollection["collectionType"];
}

export const createAssessmentCollection = async ({
  organisationId,
  workspaceId,
  name,
  collectionType,
}: CreateAssessmentCollection): Promise<{
  data: [AssessmentCollection];
}> => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Organisation-Id", organisationId as string);
  headers.append("Workspace-Id", workspaceId as string);

  const res = await fetchWrapper(`${ASSESSMENT_SERVICE_URL}/collection`, {
    headers: headers,
    method: "POST",
    body: JSON.stringify({ name, collectionType }),
    credentials: "include",
  });

  return res;
};

interface CreateAssessment {
  organisationId: string;
  workspaceId: string;
  collectionId: string;
  assessment: Partial<Assessment>;
}

export const createAssessment = async ({
  organisationId,
  workspaceId,
  collectionId,
  assessment,
}: CreateAssessment): Promise<{
  data: Partial<Assessment>;
}> => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Organisation-Id", organisationId);
  headers.append("Workspace-Id", workspaceId);

  const res = await fetchWrapper(
    `${ASSESSMENT_SERVICE_URL}/collection/${collectionId}/assessment`,
    {
      headers: headers,
      method: "POST",
      body: JSON.stringify(assessment),
      credentials: "include",
    }
  );

  return res;
};

type GetAssessmentKey = [
  string,
  string,
  "collections",
  string,
  "assessments",
  string,
];

export const getAssessmentById = async ({
  queryKey,
}: QueryFunctionContext<GetAssessmentKey>) => {
  const [, , , collectionId, , assessmentId] = queryKey;

  return client.get(
    `/collection/${collectionId}/assessment/${assessmentId}`
  ) as Promise<{
    data: DraftAssessment;
  }>;
};

export const deleteDraftCollectionById = async ({
  collectionId,
}: {
  collectionId: string;
}) => client.delete(`/collection/${collectionId}`);

export const deleteDraftAssessmentById = async ({
  collectionId,
  assessmentId,
}: {
  collectionId: string;
  assessmentId: string;
}) => {
  return client.delete(
    `/collection/${collectionId}/assessment/${assessmentId}`
  );
};

type UpdateAssessmentIntro = {
  organisationId: string;
  workspaceId: string;
  collectionId: string;
  assessmentId: string;
  introduction: Assessment["introduction"];
};

export const updateAssessmentIntro = async ({
  organisationId,
  workspaceId,
  collectionId,
  assessmentId,
  introduction,
}: UpdateAssessmentIntro) => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Organisation-Id", organisationId);
  headers.append("Workspace-Id", workspaceId);

  const res = await fetchWrapper(
    `${ASSESSMENT_SERVICE_URL}/collection/${collectionId}/assessment/${assessmentId}/intro`,
    {
      headers: headers,
      method: "PUT",
      credentials: "include",
      body: JSON.stringify(introduction),
    }
  );

  return res;
};

type UpdateAssessmentSections = {
  collectionId: string;
  assessmentId: string;
  sections: Section[];
};

export const updateAssessmentSections = async ({
  collectionId,
  assessmentId,
  sections,
}: UpdateAssessmentSections) => {
  const res = await client.put(
    `/collection/${collectionId}/assessment/${assessmentId}/sections`,
    { json: sections }
  );

  return res;
};

type CreateAssessmentSection = {
  collectionId: string;
  assessmentId: string;
};

export const createAssessmentSection = ({
  collectionId,
  assessmentId,
}: CreateAssessmentSection) => {
  return client.post(
    `/collection/${collectionId}/assessment/${assessmentId}/section`
  ) as Promise<Section>;
};

type UpdateAssessmentOutcomes = {
  organisationId: string;
  workspaceId: string;
  collectionId: string;
  assessmentId: string;
  outcomes?: Outcomes;
  evaluationType: EvaluationType;
  createDefaultOutcomes?: boolean;
};

export const updateAssessmentOutcomes = async ({
  organisationId,
  workspaceId,
  collectionId,
  assessmentId,
  outcomes,
  evaluationType,
  createDefaultOutcomes = false,
}: UpdateAssessmentOutcomes) => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Organisation-Id", organisationId);
  headers.append("Workspace-Id", workspaceId);

  const res = await fetchWrapper(
    `${ASSESSMENT_SERVICE_URL}/collection/${collectionId}/assessment/${assessmentId}/outcomes`,
    {
      headers: headers,
      method: "PUT",
      credentials: "include",
      body: JSON.stringify({
        evaluationType,
        outcomes,
        createDefaultOutcomes,
      }),
    }
  );

  return res;
};

export const listAssessmentAnswersQuery = (urlId: string) => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [organisation.id, workspace.id, "assessments", urlId, "answers"],
    queryFn: () => {
      return client.get(`/answer/${urlId}`) as Promise<{
        data: AssessmentAnswer[];
      }>;
    },
    select: (data) => data.data,
  });
};

export const downloadEvidence = async ({
  urlId,
  questionId,
  fileId,
  organisationId,
  workspaceId,
}: {
  urlId: string;
  questionId: string;
  fileId: string;
  organisationId: string;
  workspaceId: string;
}) => {
  const res = (await client.get(
    `/evidence/${urlId}/${questionId}/${fileId}?organisationId=${organisationId}&workspaceId=${workspaceId}`
  )) as { data: { fileId: string; fileName?: string; url: string }[] };
  const [file] = res.data;
  // The presigned url serves Content-Disposition: attachment, so navigating
  // to it downloads in place without leaving the page
  window.location.assign(file.url);
};

export const getResultByUrlIdAndWorkspaceIdQuery = ({
  urlId,
  organisationId,
  workspaceId,
}: {
  urlId: string;
  organisationId: string;
  workspaceId: string;
}) => {
  const { organisation, workspace } = getWorkspace();

  return queryOptions({
    queryKey: [
      organisation.id,
      workspace.id,
      "result",
      urlId,
      organisationId,
      workspaceId,
    ],
    queryFn: () =>
      client.get(
        `/result/${urlId}/organisation?organisationId=${organisationId}&workspaceId=${workspaceId}`
      ) as Promise<{ data: AssessmentResult | null }>,
    select: ({ data }) => data,
  });
};

export const archiveAssessmentCollection = ({
  collectionId,
}: {
  collectionId: string;
}) => {
  return client.delete(`/published/${collectionId}`);
};

export const publishAssessmentCollection = ({
  collectionId,
}: {
  collectionId: string;
}) => {
  return client.post(`/published/${collectionId}`);
};

export const createDraftCollectionFromPublished = ({
  collectionId,
}: {
  collectionId: string;
}) => {
  return client.post(`/published/${collectionId}/draft`) as Promise<{
    data: { collectionId: string; version: number };
  }>;
};

type BuyerAssessmentOutcomeOperation =
  | {
      action: "ASSIGN";
      name: string;
      evaluationType: EvaluationType;
      sortOrder: number;
    }
  | { action: "CLEAR" };

export const updateBuyerAssessmentOutcomeOperation = ({
  collectionId,
  assessmentId,
  outcomeId,
  payload,
}: {
  collectionId: string;
  assessmentId: string;
  outcomeId: string;
  payload: BuyerAssessmentOutcomeOperation;
}) => {
  const { organisation, workspace } = getWorkspace();
  const headers = {
    "Organisation-Id": organisation.id,
    "Workspace-Id": workspace.id,
  };

  return client.put(
    `/collection/${collectionId}/assessment/${assessmentId}/outcomes/${outcomeId}/operation`,
    { headers, json: payload }
  ) as Promise<{
    data: NonNullable<Assessment["outcomes"]>[number];
  }>;
};

export const updateCollection = ({
  collectionId,
  name,
  description,
}: {
  collectionId: string;
  name?: string;
  description?: string;
}) => {
  return client.put(`/collection/${collectionId}`, {
    json: { name, description },
  }) as Promise<{
    data: AssessmentCollection;
  }>;
};

export const overrideBuyerAssessmentResult = ({
  urlId,
  reason,
  outcomeId,
}: {
  urlId: string;
  reason: string;
  outcomeId: string;
}) => {
  return client.post(`/result/${urlId}/override`, {
    json: { outcomeId, reason },
  }) as Promise<{
    data: AssessmentCollection;
  }>;
};

export const getAssessmentResultByUrlIdQuery = (urlId?: string) =>
  queryOptions({
    queryKey: ["assessments", "result", urlId],
    enabled: Boolean(urlId),
    queryFn: () =>
      client.get(`/result/${urlId}`) as Promise<{
        data: AssessmentResult[];
      }>,
    select: (res) => (res.data.length > 0 ? res.data[0] : null),
  });
